<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import { computed, onMounted, ref, watch, type HTMLAttributes } from 'vue';
import { cn } from '@/utils';

/**
 * A phone number with a country picker, styled like {@link Input}
 * (`resources/css/tel-input.css`). Use it for every phone field.
 *
 * vue-tel-input renders a wrapper `<div>` around the real `<input>`, so
 * attributes passed the ordinary way land on the wrapper: a `<Label for>`
 * pointed at an `id` there focuses nothing, and a `placeholder` never shows.
 * `id`, `name` and `placeholder` are therefore props, handed to the library's
 * `inputOptions`, which puts them on the input itself.
 *
 * `error` mirrors `Input`: a destructive border and `aria-invalid`. The library
 * has no option for `aria-invalid`, so it is set on the input directly.
 * `aria-describedby` is a prop for the same reason as `id`: passed as an
 * attribute it would describe the wrapper, which a screen reader never reads.
 */
interface Props {
    defaultValue?: string | number;
    /** `null` renders as an empty field, for the nullable strings optional form fields carry. */
    modelValue?: string | number | null;
    id?: string;
    name?: string;
    placeholder?: string;
    /** Written `aria-describedby="…"` by callers; the id of the field's error message. */
    ariaDescribedby?: string;
    error?: string | boolean;
    class?: HTMLAttributes['class'];
}

const props = defineProps<Props>();

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string | number): void;
}>();

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: props.defaultValue,
});

/** The library wants a string; a nullable form value starts as `null`. */
const value = computed({
    get: () => (modelValue.value === null || modelValue.value === undefined ? '' : String(modelValue.value)),
    set: (next: string) => (modelValue.value = next),
});

/**
 * The library's own `inputOptions` defaults (vue-tel-input 9.3). Passing the
 * prop REPLACES its default object rather than merging into it — the merge only
 * happens when the library is installed as a plugin — so leaving these out
 * dropped `type="tel"` (which `tel-input.css` styles by) and the rest. They are
 * not exported, hence written out here.
 */
const LIBRARY_INPUT_DEFAULTS = {
    type: 'tel',
    autocomplete: 'on',
    autofocus: false,
    'aria-describedby': '',
    maxlength: 25,
    name: 'telephone',
    showDialCode: false,
    readonly: false,
    required: false,
    tabindex: 0,
    styleClasses: '',
} as const;

const inputOptions = computed(() => ({
    ...LIBRARY_INPUT_DEFAULTS,
    id: props.id ?? '',
    name: props.name ?? LIBRARY_INPUT_DEFAULTS.name,
    'aria-describedby': props.ariaDescribedby ?? LIBRARY_INPUT_DEFAULTS['aria-describedby'],
    // Blank rather than the library's English "Enter a phone number", which no
    // translation reaches; callers pass a translated placeholder when they want one.
    placeholder: props.placeholder ?? '',
}));

const root = ref<{ $el: HTMLElement } | null>(null);

const syncInvalid = () => {
    const input = root.value?.$el.querySelector('input');

    if (!input) {
        return;
    }

    if (props.error) {
        input.setAttribute('aria-invalid', 'true');
    } else {
        input.removeAttribute('aria-invalid');
    }
};

onMounted(syncInvalid);
watch(() => props.error, syncInvalid);
</script>

<template>
    <VueTelInput
        ref="root"
        v-model="value"
        valid-characters-only
        mode="international"
        :auto-default-country="false"
        default-country="ZA"
        :input-options="inputOptions"
        :class="cn(error && 'tel-input-invalid', props.class)"
    >
        <!-- The library's own arrow is a text "▼"/"▲", which shows as a stray
             glyph and is read out by screen readers. -->
        <template #arrow-icon="{ open }">
            <ChevronDown
                aria-hidden="true"
                data-testid="tel-input-arrow"
                :class="cn('text-muted-foreground ml-1 size-4 transition-transform duration-200', open && 'rotate-180')"
            />
        </template>
    </VueTelInput>
</template>
